import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Mail, Home, LogIn } from 'lucide-react';

export const AccessDenied: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const reasonParam = searchParams.get('reason');

    if (emailParam) setEmail(emailParam);
    if (reasonParam) setReason(reasonParam);
  }, [searchParams]);

  const getMessage = () => {
    switch (reason) {
      case 'not_authorized':
        return {
          title: 'Account Not Found',
          description: `Your email (${email}) is not registered in our system. Please contact an administrator to create your account.`,
          icon: <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />,
          color: 'yellow',
        };
      case 'inactive':
        return {
          title: 'Account Inactive',
          description: `Your account (${email}) has been deactivated. Please contact an administrator for assistance.`,
          icon: <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />,
          color: 'red',
        };
      default:
        return {
          title: 'Access Denied',
          description: 'You do not have permission to access this application. Please contact an administrator.',
          icon: <AlertCircle className="h-16 w-16 text-gray-500 mx-auto mb-4" />,
          color: 'gray',
        };
    }
  };

  const message = getMessage();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-md w-full">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {message.icon}

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {message.title}
          </h1>

          <p className="text-gray-600 mb-6">
            {message.description}
          </p>

          {email && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <Mail className="h-4 w-4" />
                <span className="font-medium">{email}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {reason === 'not_authorized' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="text-sm text-blue-800 font-semibold mb-2">
                  Need access?
                </p>
                <p className="text-sm text-blue-700">
                  Contact your system administrator to request an account. They will need to:
                </p>
                <ol className="text-sm text-blue-700 list-decimal list-inside mt-2 space-y-1">
                  <li>Log into the admin dashboard</li>
                  <li>Navigate to Users management</li>
                  <li>Create a new user with your email address</li>
                </ol>
              </div>
            )}

            <button
              onClick={() => navigate('/signin')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <LogIn className="h-5 w-5" />
              Try Another Account
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              <Home className="h-5 w-5" />
              Go to Home
            </button>
          </div>

          {/* Support Info */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              If you believe this is an error, please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
